import { useCallback, useEffect, useState } from 'react';

/** Seconds between code sends on every code screen (ENG-6323). */
export const RESEND_COUNTDOWN_SECONDS = 30;

interface ResendCountdown {
  /** Seconds until another code may be sent; 0 once it may. */
  secondsLeft: number;
  /** A code was just sent: start counting again. */
  restart: () => void;
}

/**
 * Paces "Resend code". Counts from mount by default, for a code screen that
 * opens right after a code was sent; `sentOnMount` false waits for `restart`.
 * Client-side and the same for every address, so it reveals nothing about
 * which accounts exist.
 */
export function useResendCountdown(sentOnMount = true): ResendCountdown {
  const [resendAt, setResendAt] = useState(() =>
    sentOnMount ? Date.now() + RESEND_COUNTDOWN_SECONDS * 1000 : 0
  );
  const [now, setNow] = useState(Date.now);
  const secondsLeft = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const counting = secondsLeft > 0;

  useEffect(() => {
    if (!counting) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [counting]);

  const restart = useCallback(() => {
    const sentAt = Date.now();
    setNow(sentAt);
    setResendAt(sentAt + RESEND_COUNTDOWN_SECONDS * 1000);
  }, []);

  return { secondsLeft, restart };
}
